// CampaignAttendeeReportingDashboard.js
import React, { Fragment, useEffect, useMemo, useState, useContext, useRef } from "react";
import { motion } from "framer-motion";
import { Card } from "react-bootstrap";
import { Button } from "@mui/material";
import { Link } from "react-router-dom";

import landmarkImg from "../../../../images/landmark/logo-text-dark.png";
import BlueLoading from "../../../../images/misc/blue-goo-loading.svg";
import PageHeaderBar from "../../../layouts/PageHeaderBar";
import { ThemeContext } from "../../../../context/ThemeContext";
import { useAuth } from "../../../Providers/LandmarkAuthContext";
import EmployeeInfo from "./EmployeeInfo";
import { useDocumentTitle } from "../../../utils";

// If your project already uses Recharts, this gives a clean line chart.
// If not installed, replace this section with your preferred charting lib.
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  ReferenceArea,
} from "recharts";

const API_URL =
  "https://betterblocks.landmarkventures.com:7770/api/landmark/salesforce/getAllCampaignAttendeeSnapshots";

const toDateKeyUTC = (d) => {
  const dt = new Date(d);
  const y = dt.getUTCFullYear();
  const m = String(dt.getUTCMonth() + 1).padStart(2, "0");
  const day = String(dt.getUTCDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

const addDaysUTC = (dateKey, deltaDays) => {
  const [y, m, d] = dateKey.split("-").map((v) => parseInt(v, 10));
  const dt = new Date(Date.UTC(y, m - 1, d));
  dt.setUTCDate(dt.getUTCDate() + deltaDays);
  return toDateKeyUTC(dt);
};

const dateFromDateKeyUTC = (dateKey) => {
  const [y, m, d] = dateKey.split("-").map((v) => parseInt(v, 10));
  return new Date(Date.UTC(y, m - 1, d));
};

const getDayLetterUTC = (dateKey) => {
  const [y, m, d] = dateKey.split("-").map((v) => parseInt(v, 10));
  const day = new Date(Date.UTC(y, m - 1, d)).getUTCDay();
  return ["S", "M", "T", "W", "T", "F", "S"][day];
};

const isWeekendUTC = (dateKey) => {
  const [y, m, d] = dateKey.split("-").map((v) => parseInt(v, 10));
  const day = new Date(Date.UTC(y, m - 1, d)).getUTCDay();
  return day === 0 || day === 6;
};

const getStartOfWeekUTC = (dateObj) => {
  const dt = new Date(Date.UTC(dateObj.getUTCFullYear(), dateObj.getUTCMonth(), dateObj.getUTCDate()));
  const day = dt.getUTCDay(); // 0 = Sunday, 1 = Monday
  const diff = day === 0 ? 6 : day - 1; // shift to Monday
  dt.setUTCDate(dt.getUTCDate() - diff);
  return toDateKeyUTC(dt);
};

const getStartOfMonthUTC = (dateObj) =>
  toDateKeyUTC(new Date(Date.UTC(dateObj.getUTCFullYear(), dateObj.getUTCMonth(), 1)));

const getStartOfYearUTC = (dateObj) => toDateKeyUTC(new Date(Date.UTC(dateObj.getUTCFullYear(), 0, 1)));

const buildDateKeysBackFromToday = (days) => {
  const todayKey = toDateKeyUTC(new Date());
  const keys = [];
  for (let i = days - 1; i >= 0; i--) keys.push(addDaysUTC(todayKey, -i));
  return keys; // oldest -> newest
};

const buildDateKeysBackFromEnd = (endKey, days) => {
  const keys = [];
  for (let i = days - 1; i >= 0; i--) keys.push(addDaysUTC(endKey, -i));
  return keys; // oldest -> newest
};
const normalizeSnapshotsToDailySeries = (snapshots = [], dateKeys = []) => {
  // snapshots: [{dateKey, totalAttendees}] (may be sparse)
  // output: [{dateKey, totalAttendees}] for every dateKey in range (carry-forward)
  const byKey = new Map();
  for (const s of snapshots) {
    if (!s?.dateKey) continue;
    byKey.set(s.dateKey, Number(s.totalAttendees ?? 0));
  }

  let last = null;
  return dateKeys.map((k) => {
    if (byKey.has(k)) last = byKey.get(k);
    return { dateKey: k, totalAttendees: last ?? 0 };
  });
};

const buildAggregateSeries = (campaignList = [], dateKeys = []) => {
  if (!dateKeys.length) return [];
  const totals = dateKeys.map((k) => ({ dateKey: k, totalAttendees: 0 }));
  for (const c of campaignList) {
    const series = normalizeSnapshotsToDailySeries(c.snapshots || [], dateKeys);
    for (let i = 0; i < series.length; i++) {
      totals[i].totalAttendees += Number(series[i].totalAttendees ?? 0);
    }
  }
  return totals;
};

const getTotalForDateKey = (snapshots = [], dateKey) => {
  if (!dateKey) return 0;
  let best = null;
  for (const s of snapshots) {
    if (!s?.dateKey) continue;
    if (s.dateKey <= dateKey && (!best || s.dateKey > best.dateKey)) {
      best = s;
    }
  }
  return Number(best?.totalAttendees ?? 0);
};

const getDeltaForRange = (snapshots = [], startKey, endKey) => {
  const startTotal = getTotalForDateKey(snapshots, startKey);
  const endTotal = getTotalForDateKey(snapshots, endKey);
  return endTotal - startTotal;
};

const getLatestSnapshotDateKey = (campaign) => {
  if (!campaign) return null;
  if (campaign?.latest?.dateKey) return campaign.latest.dateKey;
  const snaps = campaign?.snapshots || [];
  let maxKey = null;
  for (const s of snaps) {
    if (!s?.dateKey) continue;
    if (!maxKey || s.dateKey > maxKey) maxKey = s.dateKey;
  }
  return maxKey;
};

const getLatestSnapshotKeyFromCampaigns = (campaigns = []) => {
  let maxKey = null;
  for (const c of campaigns) {
    const k = getLatestSnapshotDateKey(c);
    if (k && (!maxKey || k > maxKey)) maxKey = k;
  }
  return maxKey;
};

const getLatestSnapshotDate = (campaigns = []) => {
  let latest = null;
  for (const c of campaigns) {
    const dt = c?.latest?.date ? new Date(c.latest.date) : null;
    if (dt && !Number.isNaN(dt.getTime()) && (!latest || dt > latest)) {
      latest = dt;
    }
  }
  return latest;
};

const toRateSeries = (series = []) =>
  series.map((item, idx) => {
    const prev = idx > 0 ? Number(series[idx - 1]?.totalAttendees ?? 0) : Number(item.totalAttendees ?? 0);
    const cur = Number(item.totalAttendees ?? 0);
    return {
      dateKey: item.dateKey,
      totalAttendees: idx === 0 ? 0 : cur - prev,
    };
  });

const CustomDateTick = ({ x, y, payload }) => {
  const dateKey = payload?.value;
  const weekend = isWeekendUTC(dateKey);
  const color = weekend ? "#94a3b8" : "#475569";
  return (
    <g transform={`translate(${x},${y})`}>
      <text x={0} y={0} textAnchor="middle" fill={color} fontSize={11} fontWeight={600}>
        <tspan x={0} dy={0}>
          {getDayLetterUTC(dateKey)}
        </tspan>
        <tspan x={0} dy={12}>
          {formatCompactDate(dateKey)}
        </tspan>
      </text>
    </g>
  );
};

const formatCompactDate = (dateKey) => {
  // YYYY-MM-DD -> MM/DD
  const [y, m, d] = dateKey.split("-");
  return `${m}/${d}`;
};

const getDeltaClass = (delta) => {
  if (!delta) return "delta-zero";
  const magnitude = Math.abs(delta);
  const signClass = delta > 0 ? "delta-positive" : "delta-negative";
  if (magnitude >= 8) return `${signClass} delta-strong`;
  if (magnitude >= 3) return `${signClass} delta-medium`;
  return `${signClass} delta-light`;
};

const Dashboard = () => {
  const { changeBackground } = useContext(ThemeContext);
  const { employee, isAuthenticated, loading, logout } = useAuth();

  const [apiLoading, setApiLoading] = useState(false);
  const [apiError, setApiError] = useState("");
  const [campaigns, setCampaigns] = useState([]);

  const [selectedCampaignId, setSelectedCampaignId] = useState("all");
  const [chartDays, setChartDays] = useState(14);
  const [viewMode, setViewMode] = useState("changes"); // "totals+changes" | "totals" | "changes"
  const [showAllCampaigns, setShowAllCampaigns] = useState(true);
  const [showRateOfChange, setShowRateOfChange] = useState(true);
  const [curveType, setCurveType] = useState("monotone");
  const [showLegend, setShowLegend] = useState(true);
  const [hoveredLegendKey, setHoveredLegendKey] = useState(null);
  const [pinnedLegendKey, setPinnedLegendKey] = useState(null);
  const masterTableRef = useRef(null);
  const [leaderRange, setLeaderRange] = useState("yesterday");
  const [dataView, setDataView] = useState("campaign"); // "campaign" | "owner"
  const [hoveredLineKey, setHoveredLineKey] = useState(null);
  const hoverClearTimeoutRef = useRef(null);
  const isMondayToday = useMemo(() => new Date().getUTCDay() === 1, []);
  const yesterdayLabel = isMondayToday ? "Yesterday (Friday + Weekend)" : "Yesterday";

  // set light theme once
  useEffect(() => {
    changeBackground({ value: "light", label: "Light" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useDocumentTitle("Campaign Attendee Reporting - Landmark Ventures");

  const fetchSnapshots = async () => {
    setApiError("");
    setApiLoading(true);

    try {
      const res = await fetch(API_URL, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          // If your backend uses employee auth, keep this header.
          // If not needed, it won’t hurt.
          ...(employee ? { employee } : {}),
        },
      });

      if (!res.ok) {
        const txt = await res.text().catch(() => "");
        throw new Error(`Request failed (${res.status}). ${txt}`.trim());
      }

      const json = await res.json();
      if (!json?.success || !Array.isArray(json?.data)) {
        throw new Error("Unexpected API response shape.");
      }

      // Prioritise most recent campaigns: reverse order so newest appear first.
      const reversed = [...json.data].reverse();

      setCampaigns(reversed);

      // keep "All" as default selection
    } catch (e) {
      console.error(e);
      setApiError(e?.message || "Failed to load campaign snapshots.");
    } finally {
      setApiLoading(false);
    }
  };

  // load after auth
  useEffect(() => {
    if (isAuthenticated) fetchSnapshots();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  const campaignEntities = useMemo(
    () =>
      campaigns.map((c, idx) => ({
        key: c.campaignId || c._id || `campaign_${idx}`,
        name: c.title || "(Untitled campaign)",
        url: c.campaignUrl || "",
        campaigns: [c],
      })),
    [campaigns]
  );

  const ownerEntities = useMemo(() => {
    const byOwner = new Map();
    for (const c of campaigns) {
      const ownerKey = c.ownerId || c.ownerEmail || c.ownerName || "unknown";
      const existing = byOwner.get(ownerKey) || {
        key: ownerKey,
        name: c.ownerName || c.ownerEmail || "Unassigned",
        email: c.ownerEmail || "",
        ownerId: c.ownerId || "",
        campaigns: [],
        last30Delta: 0,
      };
      existing.campaigns.push(c);
      byOwner.set(ownerKey, existing);
    }

    const latestKey = getLatestSnapshotKeyFromCampaigns(campaigns);
    const endKey = latestKey || toDateKeyUTC(new Date());
    const startKey = addDaysUTC(endKey, -30);

    for (const owner of byOwner.values()) {
      let totalDelta = 0;
      for (const c of owner.campaigns) {
        totalDelta += getDeltaForRange(c.snapshots || [], startKey, endKey);
      }
      owner.last30Delta = totalDelta;
    }

    return Array.from(byOwner.values()).sort((a, b) => {
      const aDelta = Number(a.last30Delta || 0);
      const bDelta = Number(b.last30Delta || 0);
      const aHasActivity = aDelta !== 0;
      const bHasActivity = bDelta !== 0;
      if (aHasActivity !== bHasActivity) return aHasActivity ? -1 : 1;
      const aAbs = Math.abs(aDelta);
      const bAbs = Math.abs(bDelta);
      if (aAbs !== bAbs) return bAbs - aAbs;
      return String(a.name || "").localeCompare(String(b.name || ""));
    });
  }, [campaigns]);

  const activeEntities = dataView === "campaign" ? campaignEntities : ownerEntities;

  const selectedEntity = useMemo(() => {
    if (selectedCampaignId === "all") return null;
    return activeEntities.find((e) => e.key === selectedCampaignId) || null;
  }, [activeEntities, selectedCampaignId]);

  const selectedCampaign =
    dataView === "campaign" ? selectedEntity?.campaigns?.[0] || null : null;

  const chartDateKeys = useMemo(() => {
    const latestKey = getLatestSnapshotKeyFromCampaigns(campaigns);
    const endKey = latestKey || toDateKeyUTC(new Date());
    return buildDateKeysBackFromEnd(endKey, chartDays);
  }, [campaigns, chartDays]);
  const tableDateKeys = useMemo(() => buildDateKeysBackFromToday(14), []);

  const totalChartSeriesRaw = useMemo(
    () => buildAggregateSeries(campaigns, chartDateKeys),
    [campaigns, chartDateKeys]
  );

  const selectedChartSeriesRaw = useMemo(() => {
    if (selectedCampaignId === "all") return totalChartSeriesRaw;
    if (!selectedEntity) return [];
    return buildAggregateSeries(selectedEntity.campaigns || [], chartDateKeys);
  }, [selectedCampaignId, selectedEntity, totalChartSeriesRaw, chartDateKeys]);

  const selectedChartSeries = useMemo(
    () => (showRateOfChange ? toRateSeries(selectedChartSeriesRaw) : selectedChartSeriesRaw),
    [selectedChartSeriesRaw, showRateOfChange]
  );

  const multiChartData = useMemo(() => {
    if (!activeEntities?.length) return { data: [], lines: [] };

    const palette = [
      "#2563eb",
      "#16a34a",
      "#dc2626",
      "#7c3aed",
      "#f59e0b",
      "#0ea5e9",
      "#14b8a6",
      "#f97316",
      "#64748b",
      "#22c55e",
    ];

    const compareEntities = activeEntities.slice(0, 15);
    const lines = compareEntities.map((entity, idx) => {
      const key = entity.key;
      const hitKey = `${key}__hit`;
      const rawSeries = buildAggregateSeries(entity.campaigns || [], chartDateKeys);
      const series = showRateOfChange ? toRateSeries(rawSeries) : rawSeries;
      return {
        key,
        hitKey,
        name: entity.name || "(Untitled)",
        color: palette[idx % palette.length],
        series,
      };
    });

    const data = chartDateKeys.map((k, idx) => {
      const row = { dateKey: k };
      lines.forEach((line) => {
        row[line.key] = Number(line.series[idx]?.totalAttendees ?? 0);
        row[line.hitKey] = row[line.key];
      });
      return row;
    });

    return { data, lines };
  }, [activeEntities, chartDateKeys, showRateOfChange]);

  const last15CampaignKeys = useMemo(
    () => new Set(activeEntities.slice(0, 15).map((entity) => entity.key)),
    [activeEntities]
  );

  const activeLegendKey = hoveredLineKey || hoveredLegendKey || pinnedLegendKey;

  const getLegendLabel = (lineKey) => {
    if (!lineKey) return "";
    if (showAllCampaigns) {
      const line = multiChartData.lines.find((l) => l.key === lineKey);
      return line?.name || lineKey;
    }
    if (selectedCampaignId === "all") {
      return dataView === "campaign" ? "All campaigns" : "All owners";
    }
    return selectedEntity?.name || "(Unassigned)";
  };

  const renderChartTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    if (showAllCampaigns) {
      if (!hoveredLineKey) return null;
      const hit =
        payload.find((p) => p.dataKey === hoveredLineKey) ||
        payload.find((p) => p.dataKey === `${hoveredLineKey}__hit`);
      if (!hit) return null;
      const value =
        showRateOfChange && typeof hit.value === "number"
          ? hit.value > 0
            ? `+${hit.value}`
            : `${hit.value}`
          : hit.value;
      return (
        <div className="chart-tooltip">
          <div className="chart-tooltip-label">Date: {label}</div>
          <div className="chart-tooltip-value">
            <span className="legend-swatch" style={{ background: hit.stroke }} />
            <span className="legend-label">{getLegendLabel(hoveredLineKey)}</span>
            <span className="chart-tooltip-metric">{value}</span>
          </div>
        </div>
      );
    }
    const metricLabel = showRateOfChange ? "Daily change" : "Total attendees";
    const baseValue = payload[0]?.value ?? 0;
    const displayValue =
      showRateOfChange && typeof baseValue === "number"
        ? baseValue > 0
          ? `+${baseValue}`
          : `${baseValue}`
        : baseValue;
    return (
      <div className="chart-tooltip">
        <div className="chart-tooltip-label">Date: {label}</div>
        <div className="chart-tooltip-value">
          <span className="legend-label">{metricLabel}</span>
          <span className="chart-tooltip-metric">{displayValue}</span>
        </div>
      </div>
    );
  };

  useEffect(() => {
    if (!showAllCampaigns) {
      setPinnedLegendKey(null);
      setHoveredLegendKey(null);
      return;
    }
    if (pinnedLegendKey && !last15CampaignKeys.has(pinnedLegendKey)) {
      setPinnedLegendKey(null);
    }
  }, [showAllCampaigns, pinnedLegendKey, last15CampaignKeys]);

  useEffect(() => {
    if (selectedCampaignId !== "all" && !activeEntities.find((e) => e.key === selectedCampaignId)) {
      setSelectedCampaignId("all");
      setPinnedLegendKey(null);
      setHoveredLegendKey(null);
    }
  }, [dataView, activeEntities, selectedCampaignId]);

  useEffect(() => {
    const handleClick = (e) => {
      const tableEl = masterTableRef.current;
      if (tableEl && tableEl.contains(e.target)) return;
      setPinnedLegendKey(null);
      setHoveredLegendKey(null);
      setSelectedCampaignId("all");
    };
    window.addEventListener("click", handleClick);
    return () => window.removeEventListener("click", handleClick);
  }, []);

  useEffect(() => {
    return () => {
      if (hoverClearTimeoutRef.current) {
        clearTimeout(hoverClearTimeoutRef.current);
      }
    };
  }, []);

  const masterTableRows = useMemo(() => {
    if (!activeEntities?.length) return [];

    return activeEntities.map((entity) => {
      const daily = buildAggregateSeries(entity.campaigns || [], tableDateKeys);
      const ownerUrl =
        dataView === "owner" && entity.ownerId
          ? `https://landmarkventures.lightning.force.com/lightning/r/User/${entity.ownerId}/view`
          : "";
      const row = {
        campaignId: entity.key,
        _id: entity.key,
        title: entity.name,
        campaignUrl: dataView === "campaign" ? entity.url : ownerUrl,
      };

      for (let i = 0; i < daily.length; i++) {
        const k = daily[i].dateKey;
        const total = Number(daily[i].totalAttendees ?? 0);
        const prev = i > 0 ? Number(daily[i - 1].totalAttendees ?? 0) : total;
        const delta = total - prev;

        row[`t_${k}`] = total;
        row[`d_${k}`] = delta;
      }

      return row;
    });
  }, [activeEntities, tableDateKeys, dataView]);

  const kpi = useMemo(() => {
    if (!campaigns?.length) return { totalCampaigns: 0, totalLatest: 0, totalDeltaYesterday: 0 };

    let referenceKey = null;
    for (const c of campaigns) {
      const key = getLatestSnapshotDateKey(c);
      if (key && (!referenceKey || key > referenceKey)) referenceKey = key;
    }
    const yesterdayKey = referenceKey || addDaysUTC(toDateKeyUTC(new Date()), -1);
    const yesterdayWindowDays = isMondayToday ? 3 : 1;
    const dayBeforeKey = addDaysUTC(yesterdayKey, -yesterdayWindowDays);
    let totalLatest = 0;
    let totalDeltaYesterday = 0;

    for (const c of campaigns) {
      const latest = Number(c?.latest?.totalAttendees ?? 0);
      totalLatest += latest;

      // compute delta yesterday based on snapshots within [day before, yesterday]
      const snaps = c?.snapshots || [];
      const d = getDeltaForRange(snaps, dayBeforeKey, yesterdayKey);
      totalDeltaYesterday += d;
    }

    return {
      totalCampaigns: campaigns.length,
      totalLatest,
      totalDeltaYesterday,
    };
  }, [campaigns, isMondayToday]);

  const stats = useMemo(() => {
    if (!campaigns?.length) {
      return {
        yesterdayNet: 0,
        lastWeekNet: 0,
        lastMonthNet: 0,
        topYesterday: [],
        topWeek: [],
        topMonth: [],
        ownerLeaders: [],
        referenceKey: null,
      };
    }

    const now = new Date();
    let referenceKey = null;
    for (const c of campaigns) {
      const key = getLatestSnapshotDateKey(c);
      if (key && (!referenceKey || key > referenceKey)) referenceKey = key;
    }
    const yesterdayKey = referenceKey || addDaysUTC(toDateKeyUTC(now), -1);
    const referenceDate = dateFromDateKeyUTC(yesterdayKey);
    const yesterdayWindowDays = isMondayToday ? 3 : 1;
    const dayBeforeKey = addDaysUTC(yesterdayKey, -yesterdayWindowDays);

    const thisWeekStart = getStartOfWeekUTC(referenceDate);
    const lastWeekStart = addDaysUTC(thisWeekStart, -7);
    const lastWeekEnd = addDaysUTC(thisWeekStart, -1);

    const thisMonthStart = getStartOfMonthUTC(referenceDate);
    const lastMonthEnd = addDaysUTC(thisMonthStart, -1);
    const lastMonthStart = getStartOfMonthUTC(
      new Date(Date.UTC(referenceDate.getUTCFullYear(), referenceDate.getUTCMonth() - 1, 1))
    );

    const thisYearStart = getStartOfYearUTC(referenceDate);
    const lastYearEnd = addDaysUTC(thisYearStart, -1);
    const lastYearStart = getStartOfYearUTC(new Date(Date.UTC(referenceDate.getUTCFullYear() - 1, 0, 1)));

    const campaignDeltas = campaigns.map((c) => {
      const snaps = c?.snapshots || [];
      const title = c?.title || "(Untitled campaign)";
      const yesterdayDelta = getDeltaForRange(snaps, dayBeforeKey, yesterdayKey);
      const weekDelta = getDeltaForRange(snaps, lastWeekStart, lastWeekEnd);
      const monthDelta = getDeltaForRange(snaps, lastMonthStart, lastMonthEnd);
      const currentWeekDelta = getDeltaForRange(snaps, thisWeekStart, yesterdayKey);
      const currentMonthDelta = getDeltaForRange(snaps, thisMonthStart, yesterdayKey);
      const currentYearDelta = getDeltaForRange(snaps, thisYearStart, yesterdayKey);
      const lastYearDelta = getDeltaForRange(snaps, lastYearStart, lastYearEnd);

      return {
        title,
        yesterdayDelta,
        weekDelta,
        monthDelta,
        currentWeekDelta,
        currentMonthDelta,
        currentYearDelta,
        lastYearDelta,
        ownerId: c?.ownerId || "",
        ownerName: c?.ownerName || "",
        ownerEmail: c?.ownerEmail || "",
      };
    });

    const sum = (key) => campaignDeltas.reduce((acc, c) => acc + Number(c[key] ?? 0), 0);
    const topBy = (key) =>
      campaignDeltas
        .sort((a, b) => Number(b[key]) - Number(a[key]))
        .slice(0, 30)
        .map((c) => ({ title: c.title, delta: Number(c[key]) }));

    const ownerDeltaByRange = (key) => {
      const byOwner = new Map();
      for (const c of campaignDeltas) {
        const ownerKey = c.ownerId || c.ownerEmail || c.ownerName || "unknown";
        const ownerName = c.ownerName || c.ownerEmail || "Unassigned";
        const ownerEmail = c.ownerEmail || "";
        const entry = byOwner.get(ownerKey) || { ownerKey, ownerName, ownerEmail, delta: 0 };
        entry.delta += Number(c[key] ?? 0);
        byOwner.set(ownerKey, entry);
      }
      return Array.from(byOwner.values())
        .sort((a, b) => Number(b.delta) - Number(a.delta))
        .slice(0, 30);
    };

    const ownerRangeKeyMap = {
      yesterday: "yesterdayDelta",
      currentWeek: "currentWeekDelta",
      lastWeek: "weekDelta",
      currentMonth: "currentMonthDelta",
      lastMonth: "monthDelta",
      currentYear: "currentYearDelta",
      lastYear: "lastYearDelta",
    };

    return {
      yesterdayNet: sum("yesterdayDelta"),
      lastWeekNet: sum("weekDelta"),
      lastMonthNet: sum("monthDelta"),
      topYesterday: topBy("yesterdayDelta"),
      topWeek: topBy("weekDelta"),
      topMonth: topBy("monthDelta"),
      ownerLeaders: ownerDeltaByRange(ownerRangeKeyMap[leaderRange] || "yesterdayDelta"),
      referenceKey,
    };
  }, [campaigns, leaderRange, isMondayToday]);

  const lastSnapshotLabel = useMemo(() => {
    const latest = getLatestSnapshotDate(campaigns);
    if (!latest) return "Last snapshot: --";
    const formatter = new Intl.DateTimeFormat("en-US", {
      timeZone: "America/New_York",
      year: "numeric",
      month: "short",
      day: "2-digit",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
    return `Last snapshot: ${formatter.format(latest)} ET`;
  }, [campaigns]);

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center p-5">
        <div className="spinner-border" role="status" aria-label="Loading" />
      </div>
    );
  }

  return (
    <Fragment>
      <div className="global-dash-wrapper campaign-attendee-reporting-dash">
        <div className="d-flex align-items-center justify-content-center">
          <img src={landmarkImg} className="img-fluid landmark-img" alt="Landmark" />
        </div>

        {isAuthenticated && employee && (
          <div className="landmark-sign-in" style={{ marginBottom: "18px" }}>
            <div className="existing-accounts-container">
              <EmployeeInfo
                landmarkEmployee={employee}
                disableHover={true}
                showLogout={true}
                logout={logout}
              />
            </div>
          </div>
        )}

        <PageHeaderBar
          pages={[
            { title: "Landmark", url: `` },
            { title: "Campaign Attendee Reporting", url: `/landmark/salesforce/campaign-attendee-reporting` },
          ]}
        />

        <div className="campaign-attendee-reporting-body">
          {!isAuthenticated ? (
            <div className="text-center text-muted p-5">Please sign in to access the dashboard.</div>
          ) : (
            <>
              {apiLoading ? (
                <div className="loading-state">
                  <div className="loading-text">Loading Campaign Snapshots...</div>
                  <img src={BlueLoading} className="loading-spinner" alt="Loading" />
                </div>
              ) : apiError ? (
                <Card className="mb-3 p-3 admin-card">
                  <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
                    <div>
                      <div className="fw-bold">Could not load attendee snapshots</div>
                      <div className="text-muted small">{apiError}</div>
                    </div>
                    <Button variant="contained" onClick={fetchSnapshots}>
                      Retry
                    </Button>
                  </div>
                </Card>
              ) : (
                <>
                  <motion.div animate={{ x: 0 }} initial={{ x: 150 }}>
                    <Card className="mb-3 p-3 kpi-card admin-card">
                      <div className="kpi-row">
                        <div className="kpi-item">
                          <div className="kpi-label">Campaigns</div>
                          <div className="kpi-value">{kpi.totalCampaigns}</div>
                        </div>
                        <div className="kpi-item">
                          <div className="kpi-label">Total latest attendees</div>
                          <div className="kpi-value">{kpi.totalLatest}</div>
                        </div>

                        <div className="kpi-actions">
                          <div className="snapshot-meta">
                            <div className="snapshot-label">Snapshots run daily at 11:00pm ET</div>
                            <div className="snapshot-value">{lastSnapshotLabel}</div>
                          </div>
                          <Button size="small" variant="outlined" onClick={fetchSnapshots}>
                            Refresh
                          </Button>
                        </div>
                      </div>
                    </Card>
                  </motion.div>

                <motion.div animate={{ x: 0 }} initial={{ x: 150 }}>
                  <Card className="mb-3 p-3 admin-card chart-card">
                      <div className="section-title">Snapshot stats</div>
                    <div className="snapshot-description">
                        Snapshots are captured nightly at 11:00pm ET from Salesforce campaign members with status
                        "Attending".
                    </div>
                      <div className="stats-grid">
                        <div className="stat-card">
                          <div className="stat-label">Net change</div>
                          <div className="net-change-rows">
                            <div className="net-change-row">
                              <span>{yesterdayLabel}</span>
                              <span
                                className={`stat-value ${
                                  stats.yesterdayNet > 0 ? "positive" : stats.yesterdayNet < 0 ? "negative" : ""
                                }`}
                              >
                                {stats.yesterdayNet > 0 ? "+" : ""}
                                {stats.yesterdayNet}
                              </span>
                            </div>
                            <div className="net-change-row">
                              <span>Last week</span>
                              <span
                                className={`stat-value ${
                                  stats.lastWeekNet > 0 ? "positive" : stats.lastWeekNet < 0 ? "negative" : ""
                                }`}
                              >
                                {stats.lastWeekNet > 0 ? "+" : ""}
                                {stats.lastWeekNet}
                              </span>
                            </div>
                            <div className="net-change-row">
                              <span>Last month</span>
                              <span
                                className={`stat-value ${
                                  stats.lastMonthNet > 0 ? "positive" : stats.lastMonthNet < 0 ? "negative" : ""
                                }`}
                              >
                                {stats.lastMonthNet > 0 ? "+" : ""}
                                {stats.lastMonthNet}
                              </span>
                            </div>
                          </div>
                        </div>
                        <div className="stat-card stat-gainers">
                          <div className="stat-label">Top gainers {yesterdayLabel}</div>
                          <ul className="gainers-list">
                            {stats.topYesterday.length ? (
                              stats.topYesterday.map((g) => (
                                <li key={`yesterday-${g.title}`}>
                                  <span className="gainer-title" title={g.title}>
                                    {g.title}
                                  </span>
                                  <span
                                    className={`gainer-delta ${g.delta === 0 ? "zero" : ""}`}
                                  >
                                    {g.delta > 0 ? `+${g.delta}` : g.delta}
                                  </span>
                                </li>
                              ))
                            ) : (
                              <li className="muted">No gains {yesterdayLabel}</li>
                            )}
                          </ul>
                        </div>
                        <div className="stat-card stat-gainers">
                          <div className="stat-label">Top gainers last week</div>
                          <ul className="gainers-list">
                            {stats.topWeek.length ? (
                              stats.topWeek.map((g) => (
                                <li key={`week-${g.title}`}>
                                  <span className="gainer-title" title={g.title}>
                                    {g.title}
                                  </span>
                                  <span
                                    className={`gainer-delta ${g.delta === 0 ? "zero" : ""}`}
                                  >
                                    {g.delta > 0 ? `+${g.delta}` : g.delta}
                                  </span>
                                </li>
                              ))
                            ) : (
                              <li className="muted">No gains last week</li>
                            )}
                          </ul>
                        </div>
                        <div className="stat-card stat-gainers">
                          <div className="stat-label stat-label-inline">
                            <span>RSVPs Campaign Leaders</span>
                            <select
                              className="stat-label-select"
                              value={leaderRange}
                              onChange={(e) => setLeaderRange(e.target.value)}
                            >
                              <option value="yesterday">Yesterday</option>
                              <option value="currentWeek">This week</option>
                              <option value="lastWeek">Last week</option>
                              <option value="currentMonth">This month</option>
                              <option value="lastMonth">Last month</option>
                              <option value="currentYear">This year</option>
                              <option value="lastYear">Last year</option>
                            </select>
                          </div>
                          <ul className="gainers-list">
                            {stats.ownerLeaders.length ? (
                              stats.ownerLeaders.map((g) => (
                                <li key={`owner-${g.ownerKey}`}>
                                  <span className="gainer-title" title={g.ownerName}>
                                    {g.ownerName}
                                  </span>
                                  <span className={`gainer-delta ${g.delta === 0 ? "zero" : ""}`}>
                                    {g.delta > 0 ? `+${g.delta}` : g.delta}
                                  </span>
                                </li>
                              ))
                            ) : (
                              <li className="muted">No leaders for range</li>
                            )}
                          </ul>
                        </div>
                      </div>
                    </Card>
                  </motion.div>

                <motion.div animate={{ x: 0 }} initial={{ x: 150 }}>
                  <div className="view-switch">
                    <span className="view-switch-label">View</span>
                    <div className="toggle-group">
                      <button
                        type="button"
                        className={`toggle-btn ${dataView === "campaign" ? "active" : ""}`}
                        onClick={() => setDataView("campaign")}
                      >
                        Campaigns
                      </button>
                      <button
                        type="button"
                        className={`toggle-btn ${dataView === "owner" ? "active" : ""}`}
                        onClick={() => setDataView("owner")}
                      >
                        Owners
                      </button>
                    </div>
                  </div>
                </motion.div>

                  <motion.div animate={{ x: 0 }} initial={{ x: 150 }}>
                    <Card className="mb-3 p-3 admin-card">
                      <div className="section-title">Campaign trend</div>

                      <div className="controls-row">
                      <label className="select-field">
                        <span className="select-label">{dataView === "campaign" ? "Campaign" : "Owner"}</span>
                        <select
                          className="select-control"
                          value={selectedCampaignId}
                          onChange={(e) => {
                            const next = e.target.value;
                            setSelectedCampaignId(next);
                            if (next !== "all") setShowAllCampaigns(false);
                          }}
                        >
                          <option value="all">
                            {dataView === "campaign" ? "All campaigns" : "All owners"}
                          </option>
                          {activeEntities.map((entity) => (
                            <option key={entity.key} value={entity.key}>
                              {entity.name || "(Unassigned)"}
                            </option>
                          ))}
                        </select>
                      </label>

                      <label className="select-field">
                        <span className="select-label">Chart range</span>
                        <select
                          className="select-control"
                          value={chartDays}
                          onChange={(e) => setChartDays(Number(e.target.value))}
                        >
                          {[7, 14, 30, 60, 90, 180, 365].map((d) => (
                            <option key={d} value={d}>
                              Past {d} days
                            </option>
                          ))}
                        </select>
                      </label>

                      <label className="select-field">
                        <span className="select-label">Line style</span>
                        <select
                          className="select-control"
                          value={curveType}
                          onChange={(e) => setCurveType(e.target.value)}
                        >
                          <option value="monotone">Smooth</option>
                          <option value="linear">Straight</option>
                          <option value="natural">Soft</option>
                          <option value="step">Step</option>
                          <option value="stepBefore">Step before</option>
                          <option value="stepAfter">Step after</option>
                        </select>
                      </label>

                      </div>

                      <div className="controls-row controls-row-secondary">
                      <label className="checkbox-field">
                        <input
                          type="checkbox"
                          checked={showAllCampaigns}
                          onChange={(e) => setShowAllCampaigns(e.target.checked)}
                        />
                        <span>
                          Compare last 15 {dataView === "campaign" ? "campaigns" : "owners"}
                        </span>
                      </label>

                      <label className="checkbox-field">
                        <input
                          type="checkbox"
                          checked={showRateOfChange}
                          onChange={(e) => setShowRateOfChange(e.target.checked)}
                        />
                        <span>Rate of change</span>
                      </label>

                      <label className="checkbox-field">
                        <input
                          type="checkbox"
                          checked={showLegend}
                          onChange={(e) => setShowLegend(e.target.checked)}
                        />
                        <span>Legend</span>
                      </label>

                      {dataView === "campaign" && selectedCampaign?.campaignUrl ? (
                          <a
                            href={selectedCampaign.campaignUrl}
                            target="_blank"
                            rel="noreferrer"
                            className="campaign-open-link"
                          >
                            Open in Salesforce
                          </a>
                        ) : null}
                      </div>

                    {!selectedEntity && selectedCampaignId !== "all" && !showAllCampaigns ? (
                      <div className="text-muted small">
                        Select a {dataView === "campaign" ? "campaign" : "owner"} to view trends.
                      </div>
                    ) : (
                        <div className="chart-wrap">
                          <ResponsiveContainer width="100%" height={320}>
                          <LineChart
                            data={showAllCampaigns ? multiChartData.data : selectedChartSeries}
                            margin={{ top: 10, right: 20, bottom: 0, left: 0 }}
                            onMouseLeave={() => setHoveredLineKey(null)}
                          >
                            <CartesianGrid strokeDasharray="3 3" />
                            {chartDateKeys
                              .filter((k) => isWeekendUTC(k))
                              .map((k) => (
                                <ReferenceArea
                                  key={`weekend-${k}`}
                                  x1={k}
                                  x2={addDaysUTC(k, 1)}
                                  fill="rgba(148, 163, 184, 0.12)"
                                  strokeOpacity={0}
                                />
                              ))}
                              <XAxis
                                dataKey="dateKey"
                              tick={<CustomDateTick />}
                              interval={0}
                              minTickGap={8}
                              tickMargin={10}
                              />
                            <YAxis
                              allowDecimals={false}
                              label={{
                                value: showRateOfChange ? "Daily change" : "Attendees",
                                angle: -90,
                                position: "insideLeft",
                                fill: "#475569",
                                fontSize: 12,
                                fontWeight: 600,
                              }}
                            />
                            <Tooltip content={renderChartTooltip} isAnimationActive={false} />
                            {showAllCampaigns ? (
                              <>
                                {multiChartData.lines.map((line) => (
                                  <Line
                                    key={`${line.key}-hit`}
                                    type="linear"
                                    dataKey={line.hitKey}
                                    stroke="transparent"
                                    strokeWidth={16}
                                    strokeLinecap="round"
                                    dot={false}
                                    activeDot={false}
                                    isAnimationActive={false}
                                    legendType="none"
                                    onMouseEnter={() => {
                                      if (hoverClearTimeoutRef.current) {
                                        clearTimeout(hoverClearTimeoutRef.current);
                                      }
                                      setHoveredLineKey(line.key);
                                    }}
                                    onMouseLeave={() => {
                                      if (hoverClearTimeoutRef.current) {
                                        clearTimeout(hoverClearTimeoutRef.current);
                                      }
                                      hoverClearTimeoutRef.current = setTimeout(() => {
                                        setHoveredLineKey(null);
                                      }, 120);
                                    }}
                                  />
                                ))}
                                {multiChartData.lines.map((line) => (
                                  <Line
                                    key={line.key}
                                    type={curveType}
                                    dataKey={line.key}
                                    stroke={line.color}
                                    strokeWidth={activeLegendKey === line.key ? 3 : 2}
                                    strokeOpacity={
                                      activeLegendKey && activeLegendKey !== line.key ? 0.25 : 1
                                    }
                                    dot={false}
                                    isAnimationActive={false}
                                  />
                                ))}
                              </>
                            ) : (
                              <Line
                                type={curveType}
                                dataKey="totalAttendees"
                                strokeWidth={2}
                                dot={false}
                                isAnimationActive={false}
                              />
                            )}
                            </LineChart>
                          </ResponsiveContainer>
                        {showLegend && (
                          <div className="chart-legend">
                            {showAllCampaigns ? (
                              multiChartData.lines.map((line) => (
                                <div
                                  key={`legend-${line.key}`}
                                  className={`legend-item ${
                                    activeLegendKey && activeLegendKey !== line.key ? "legend-dimmed" : ""
                                  }`}
                                  title={line.name}
                                  onMouseEnter={() => {
                                    setSelectedCampaignId("all");
                                    setPinnedLegendKey(null);
                                    setHoveredLegendKey(line.key);
                                  }}
                                  onMouseLeave={() => setHoveredLegendKey(null)}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setPinnedLegendKey(line.key);
                                    setSelectedCampaignId(line.key);
                                  }}
                                >
                                  <span className="legend-swatch" style={{ background: line.color }} />
                                  <span className="legend-label">{line.name}</span>
                                </div>
                              ))
                            ) : (
                              <div className="legend-item">
                                <span className="legend-swatch" style={{ background: "#2563eb" }} />
                                <span className="legend-label">
                                  {selectedCampaignId === "all"
                                    ? dataView === "campaign"
                                      ? "All campaigns"
                                      : "All owners"
                                    : selectedEntity?.name || "(Unassigned)"}
                                </span>
                              </div>
                            )}
                          </div>
                        )}
                        </div>
                      )}
                    </Card>
                  </motion.div>

                  <motion.div animate={{ x: 0 }} initial={{ x: 150 }}>
                    <Card className="mb-3 p-3 admin-card">
                      <div className="master-table-header">
                        <div className="master-table-text">
                          <div className="section-title">
                            Master view (all {dataView === "campaign" ? "campaigns" : "owners"})
                          </div>
                          <div className="text-muted small master-table-description">
                            Totals are shown per day. If a {dataView === "campaign" ? "campaign" : "owner"} gains or
                            loses attendees versus the prior day, the delta is shown in green or red.
                            {dataView === "campaign"
                              ? " Campaign names link directly to Salesforce."
                              : ""}
                          </div>
                        </div>
                        <div className="table-toolbar">
                          <div className="toggle-group">
                            <button
                              type="button"
                              className={`toggle-btn ${viewMode === "totals+changes" ? "active" : ""}`}
                              onClick={() => setViewMode("totals+changes")}
                            >
                              Totals + changes
                            </button>
                            <button
                              type="button"
                              className={`toggle-btn ${viewMode === "totals" ? "active" : ""}`}
                              onClick={() => setViewMode("totals")}
                            >
                              Totals only
                            </button>
                            <button
                              type="button"
                              className={`toggle-btn ${viewMode === "changes" ? "active" : ""}`}
                              onClick={() => setViewMode("changes")}
                            >
                              Changes only
                            </button>
                          </div>
                        </div>
                      </div>

                      <div className="master-table-wrap" ref={masterTableRef}>
                        <table className="master-table">
                          <thead>
                            <tr className="dow-row">
                              <th className="campaign-col">
                                {dataView === "campaign" ? "Campaign" : "Owner"}
                              </th>
                              {tableDateKeys.map((k) => (
                                <th
                                  key={`date-${k}`}
                                  className={`date-col-head ${isWeekendUTC(k) ? "weekend" : ""}`}
                                >
                                  <span className="date-dow">{getDayLetterUTC(k)}</span>
                                  <span className="date-md">{formatCompactDate(k)}</span>
                                </th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {masterTableRows.map((row) => {
                              const rowKey = row?.campaignId || row?._id;
                              const isSelected =
                                rowKey === selectedCampaignId || (activeLegendKey && rowKey === activeLegendKey);
                              const isDimmed = activeLegendKey && rowKey !== activeLegendKey;
                              return (
                                <tr
                                  key={rowKey}
                                  className={`${isSelected ? "row-selected" : ""} ${isDimmed ? "row-dimmed" : ""}`}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    if (rowKey) {
                                      if (rowKey === selectedCampaignId) {
                                        setSelectedCampaignId("all");
                                        setPinnedLegendKey(null);
                                        setHoveredLegendKey(null);
                                      } else {
                                        setSelectedCampaignId(rowKey);
                                        setHoveredLegendKey(null);
                                        if (last15CampaignKeys.has(rowKey)) {
                                          setPinnedLegendKey(rowKey);
                                        } else {
                                          setPinnedLegendKey(null);
                                        }
                                      }
                                    }
                                  }}
                                >
                                  <td className="campaign-col">
                                    {row.campaignUrl ? (
                                      <a
                                        className="campaign-link"
                                        href={row.campaignUrl}
                                        target="_blank"
                                        rel="noreferrer"
                                      >
                                        {row.title || "(Untitled campaign)"}
                                      </a>
                                    ) : (
                                      <span className="text-muted">{row.title || "(Untitled campaign)"}</span>
                                    )}
                                  </td>
                                  {tableDateKeys.map((k) => {
                                    const total = Number(row[`t_${k}`] ?? 0);
                                    const delta = Number(row[`d_${k}`] ?? 0);
                                    const deltaText = delta === 0 ? "-" : `${delta}`;
                                    const weekendClass = isWeekendUTC(k) ? "weekend-cell" : "";
                                    const deltaClass = getDeltaClass(delta);
                                    const showTotals = viewMode !== "changes";
                                    const showDeltas = viewMode !== "totals";
                                    const showZeroDash = viewMode === "changes";
                                    const showDeltaValue = delta !== 0 && showDeltas;

                                    return (
                                      <td
                                        key={`${rowKey}-${k}`}
                                        className={`date-col-body ${weekendClass} ${deltaClass}`}
                                      >
                                        <div className="day-cell">
                                          {showTotals && <span className="day-total">{total}</span>}
                                          {delta === 0 ? (
                                            showZeroDash ? (
                                              <span className="delta-text delta-zero-text">{deltaText}</span>
                                            ) : null
                                          ) : showDeltaValue ? (
                                            <span className="delta-text">
                                              <span className="delta-sign">{delta > 0 ? "+" : "-"}</span>
                                              <span className="delta-number">{Math.abs(delta)}</span>
                                            </span>
                                          ) : null}
                                        </div>
                                      </td>
                                    );
                                  })}
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </Card>
                  </motion.div>
                </>
              )}
            </>
          )}
        </div>
      </div>
    </Fragment>
  );
};

export default Dashboard;
